import React, { useState, lazy, Suspense, useContext, useEffect, useRef } from "react";
import {
  MDBContainer, MDBNavbar, MDBIcon
} from "mdb-react-ui-kit";
import Button from "../../../Components/Button";
import { UserContext } from "../../../Context/UserContext";
import useLogout from "../../../Hooks/useLogout.js";
import { useSidebar } from "../../../Context/SidebarContext.js";
import LoadingOverlay from "../../../Components/LoadingOverlay.js";
import useLaravelEcho from "../../../Hooks/useLaravelEcho.js";
import Toast from "../../../Components/Toast.js";
import useAxiosCall from "../../../Hooks/useAxiosCall.js";
import { useLoadingOverlay } from "../../../Context/LoadingOverlayContext.js";
import { encryptData } from "../../../Utilities/CryptoUtility.js";
import { useNotification } from '../../../Context/NotificationContext';
import useNotifications from "../../../Hooks/useNotifications";
import { ThemeContext } from "../../../Context/ThemeContext.js";
import { useDomain } from "../../../Context/DomainContext";

const UserMenu = lazy(() => import('./UserMenu'));
const ChangePasswordModal = lazy(() => import('./ChangePasswordModal'));

const Header = () => {
  const { isSidebarOpen, toggleSidebarWithOppositeState } = useSidebar();
  const { user } = useContext(UserContext);
  const { isDarkUi } = useContext(ThemeContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { handleLogout } = useLogout();
  const echo = useLaravelEcho();
  const [toastData, setToastData] = useState({ type: "", message: "", isOpen: false });
  const { makeApiCall } = useAxiosCall();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { domain } = useDomain();
  const {notifications, setNotifications , notificationsFetch ,setNotificationsFetch, notificationsCount, setNotificationsCount } = useNotification();
  const { fetchNotifications } = useNotifications();
  const domainRef = useRef(domain);

  useEffect(() => {
    domainRef.current = domain;
  }, [domain]);

  //to handle logout if logout required even is triggered
  useEffect(() => {
    const logoutRequiredHandler = () => {
      setToastData({
        type: "danger",
        message: "You've been logged out. Your User was logged in on another device or browser. To avoid this, only log in with one device or browser at a time.",
        isOpen: true,
        position: 'top-center'
      });
      showLoadingOverlay();
      setTimeout(() => {
        hideLoadingOverlay();
        handleLogout();
      }, 5000);
    };
    window.addEventListener('logoutRequired', logoutRequiredHandler);

    return () => {
      window.removeEventListener('logoutRequired', logoutRequiredHandler);
    };
  }, [handleLogout]);

  useEffect(() => {
    if (echo && user) {
      let uname=user.name;
      let id = uname != undefined ? uname.replace(/[^a-zA-Z0-9_-]+/, "") : "";
      echo.private(`channel-` + id).listen("ExportNotification", (response) => {
        setNotificationsFetch(true);
        fetchNotifications(1, true, domainRef.current);
        setToastData({
          type: "info",
          message: (
            <span>
              File generated successfully, click
              <div
                onClick={() => downloadReport(response.file_id, response.file_name)}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'blue',
                  display: 'inline',
                  marginLeft: '4px',
                  marginRight: '4px'
                }}
              >
                here
              </div>
              to download.
            </span>
          ),
          isOpen: true,
          position: 'top-right'
        });
      });
    }
  }, [echo, user]);

  const handleApiCallSuccess = (data, file_id, file_name) => {
    try {
      const blob = new Blob([data], { type: data.type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error handling API call success:', error);
    }
  };

  const handleApiCallError = (errorMessage) => {
    console.error('Error in header:', errorMessage);
  };

  const downloadReport = async (file_id, file_name) => {
    const requestData = {
      data: encryptData(JSON.stringify(
        {
          file_id: file_id,
          domain : domain
        }
      ))
    };
    await makeApiCall({
      requestData,
      onSuccess: (data) => handleApiCallSuccess(data, file_id, file_name),
      onError: handleApiCallError,
      requestType: 'POST',
      url: "downloadReport",
      loader: true,
      responseType: 'blob'
    });
  }

  const resetToastData = () => {
    setToastData({ type: "", message: "", isOpen: false });
  }

  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const sidebarColor = isSidebarOpen ? 'secondary' : 'light'

  return (
    <Suspense fallback={<LoadingOverlay />}>
      <Toast toast={toastData} resetToast={resetToastData} hasHeader={false} />
      <MDBNavbar
        expand="lg"
        dark={isDarkUi}
        light={!isDarkUi}
        className={isDarkUi ? "theme-navbar-dark" : "theme-navbar-light"}
        fixed="top"
        style={{ height: '62px' }}
      >

        <MDBContainer fluid>
          <Button onClick={toggleSidebarWithOppositeState} className="" color={sidebarColor} >
            <MDBIcon icon="bars" size="lg" fas />
          </Button>
          <h1 className="h5 mt-2 headerWelcomeMessage">
            {user.preferredName ? `Welcome ${user.preferredName} to PropertyBoss Online` : 'Welcome to PropertyBoss Online'}
          </h1>
          <UserMenu onPasswordChange={toggleModal} onLogout={handleLogout} firstName={user ? user.givenName : ''} surName={user ? user.surname : ''} />
        </MDBContainer>
      </MDBNavbar>
      <ChangePasswordModal isOpen={isModalOpen} onClose={toggleModal} />
    </Suspense>
  );
}

export default Header;
